/* variant-cascade-driver.js (Option-1-as-driver, bulletproof)
   - Option 1: never disabled (driver)
   - Option 2: filtered by Option 1
   - Option 3: filtered by Option 1 + Option 2
   - Normalized comparisons; fallback to "exists" if "available" yields nothing
*/
(function () {
  let BUSY = false;
  const FILTER_BY_AVAILABILITY = true; // strict first, then fallback to "exists" if empty

  const n = (s) => String(s ?? '').trim().toLowerCase();

  function cssEscape(s) {
    if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(s);
    return String(s).replace(/([^\w-])/g, '\\$1');
  }

  function sectionIdOf(root) {
    return root.getAttribute('data-section') ||
           (root.closest('.shopify-section')?.id || '').replace('shopify-section-','') || '';
  }

  function variantsJSON(root) {
    const s = root.querySelector('script[type="application/json"]');
    if (!s || !s.textContent) return [];
    try { return JSON.parse(s.textContent) || []; } catch { return []; }
  }

  function optionNames(root) {
    // Prefer data-option-position to lock the order 1→2→3
    const map = new Map();
    root.querySelectorAll('[name^="options["]').forEach(el => {
      const m = el.name.match(/^options\[(.*)\]$/);
      if (!m) return;
      const name = m[1];
      if (!map.has(name)) {
        const pos = Number(el.getAttribute('data-option-position')) || 999;
        map.set(name, { name, pos });
      }
    });
    return Array.from(map.values())
      .sort((a,b) => a.pos - b.pos)
      .map(x => x.name);
  }

  function readSel(root, names) {
    const out = {};
    names.forEach(name => {
      const r = root.querySelector(`input[type="radio"][name="options[${cssEscape(name)}]"]:checked`);
      if (r) { out[name] = r.value; return; }
      const s = root.querySelector(`select[name="options[${cssEscape(name)}]"]`);
      if (s) out[name] = s.value;
    });
    return out;
  }

  function eachValueInDOM(root, name, cb) {
    // radios/swatches
    root.querySelectorAll(`input[type="radio"][name="options[${cssEscape(name)}]"]`).forEach(r => cb(r.value, r));
    // selects
    const sel = root.querySelector(`select[name="options[${cssEscape(name)}]"]`);
    if (sel) Array.from(sel.options).forEach(o => cb(o.value, o));
  }

  function setDisabled(root, name, value, disabled, neverDisable=false) {
    const valueN = n(value);
    if (neverDisable) disabled = false;

    // radios/swatches
    root.querySelectorAll(`input[type="radio"][name="options[${cssEscape(name)}]"]`).forEach(r => {
      if (n(r.value) !== valueN) return;
      if (disabled) {
        r.setAttribute('disabled','');
        r.setAttribute('aria-disabled','true');
        r.classList.add('disabled');
        const lab = root.querySelector(`label[for="${cssEscape(r.id)}"]`);
        if (lab) { lab.classList.add('disabled'); lab.setAttribute('aria-disabled','true'); }
      } else {
        r.removeAttribute('disabled');
        r.setAttribute('aria-disabled','false');
        r.classList.remove('disabled');
        const lab = root.querySelector(`label[for="${cssEscape(r.id)}"]`);
        if (lab) { lab.classList.remove('disabled'); lab.removeAttribute('aria-disabled'); }
      }
    });

    // selects
    const sel = root.querySelector(`select[name="options[${cssEscape(name)}]"]`);
    if (sel) {
      Array.from(sel.options).forEach(o => {
        if (n(o.value) !== valueN) return;
        o.disabled = !!disabled && !neverDisable;
      });
    }
  }

  function clickFirstEnabled(root, name) {
    const radios = Array.from(root.querySelectorAll(`input[type="radio"][name="options[${cssEscape(name)}]"]`));
    const first = radios.find(r => !r.disabled);
    if (first) {
      if (!first.checked) first.checked = true;
      first.dispatchEvent(new Event('input',{bubbles:true}));
      first.dispatchEvent(new Event('change',{bubbles:true}));
      return true;
    }
    const sel = root.querySelector(`select[name="options[${cssEscape(name)}]"]`);
    if (sel) {
      const opt = Array.from(sel.options).find(o => !o.disabled);
      if (opt && sel.value !== opt.value) {
        sel.value = opt.value;
        sel.dispatchEvent(new Event('input',{bubbles:true}));
        sel.dispatchEvent(new Event('change',{bubbles:true}));
        return true;
      }
    }
    return false;
  }

  function setChoice(root, name, value) {
    const valueN = n(value);
    const radios = Array.from(root.querySelectorAll(`input[type="radio"][name="options[${cssEscape(name)}]"]`));
    const r = radios.find(x => !x.disabled && n(x.value) === valueN);
    if (r) {
      if (!r.checked) r.checked = true;
      r.dispatchEvent(new Event('input',{bubbles:true}));
      r.dispatchEvent(new Event('change',{bubbles:true}));
      return true;
    }
    const sel = root.querySelector(`select[name="options[${cssEscape(name)}]"]`);
    if (sel && !sel.disabled && n(sel.value) !== valueN) {
      const opt = Array.from(sel.options).find(o => !o.disabled && n(o.value) === valueN);
      if (opt) {
        sel.value = opt.value;
        sel.dispatchEvent(new Event('input',{bubbles:true}));
        sel.dispatchEvent(new Event('change',{bubbles:true}));
        return true;
      }
    }
    return false;
  }

  function allowedSet(variants, idx, constraints, onlyAvailable) {
    const out = new Set();
    variants.forEach(v => {
      if (!v) return;
      if (onlyAvailable && v.available === false) return;
      let ok = true;
      for (let j = 0; j < constraints.length; j++) {
        const want = constraints[j];
        if (want == null) continue;
        if (n(v.options[j]) !== n(want)) { ok = false; break; }
      }
      if (ok) out.add(v.options[idx]);
    });
    return out;
  }

  function findVariant(variants, values) {
    return variants.find(v => v && v.options.length === values.length &&
      v.options.every((val, i) => n(val) === n(values[i])));
  }

  function finalize(root, names, variants) {
    const sel = readSel(root, names);
    const values = names.map(nm => sel[nm]);
    const variant = findVariant(variants, values) || null;

    if (variant) {
      // update hidden id inputs
      document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(inp => {
        if (inp.disabled) inp.disabled = false;
        if (String(inp.value) !== String(variant.id)) {
          inp.value = variant.id;
          inp.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      // update URL
      try {
        const u = new URL(location.href);
        u.searchParams.set('variant', variant.id);
        history.replaceState({}, '', u.toString());
      } catch {}
    }

    // theme events
    const sec = sectionIdOf(root);
    const payload = { sectionId: sec, variant };
    const ev1 = new CustomEvent('variant-change', { bubbles: true, detail: payload });
    const ev2 = new CustomEvent('variant:change', { bubbles: true, detail: payload });
    document.dispatchEvent(ev1); document.dispatchEvent(ev2);
    const secEl = document.getElementById('shopify-section-' + sec) || root.closest('.shopify-section');
    if (secEl) { secEl.dispatchEvent(ev1); secEl.dispatchEvent(ev2); }
  }

  function recalc(root) {
    if (BUSY) return;
    BUSY = true;

    const variants = variantsJSON(root);
    const names = optionNames(root);
    if (!variants.length || !names.length) { BUSY = false; return; }

    const [name1, name2, name3] = names;
    const sel = readSel(root, names);

    // --- Option 1: driver, never disabled ---
    eachValueInDOM(root, name1, (val) => setDisabled(root, name1, val, false, true));

    // --- Option 2: by Option 1 ---
    if (name2 && sel[name1] != null) {
      let allow2 = allowedSet(variants, 1, [sel[name1]], FILTER_BY_AVAILABILITY);
      if (FILTER_BY_AVAILABILITY && allow2.size === 0) {
        // fallback to existence (don't hard-grey everything out)
        allow2 = allowedSet(variants, 1, [sel[name1]], false);
      }
      // toggle states based on allow2
      const allow2N = new Set(Array.from(allow2).map(n));
      eachValueInDOM(root, name2, (val) => {
        const disable = !allow2N.has(n(val));
        setDisabled(root, name2, val, disable, false);
      });
      // ensure a valid selection exists
      const current2 = sel[name2];
      if (!current2 || !allow2N.has(n(current2))) {
        const first = Array.from(allow2)[0];
        if (first != null) setChoice(root, name2, first);
        else clickFirstEnabled(root, name2);
      }
    }

    // --- Option 3: by Option 1 + 2 ---
    if (name3 && sel[name1] != null) {
      const c2 = name2 ? readSel(root, [name2])[name2] : undefined;
      let allow3 = allowedSet(variants, 2, [sel[name1], c2], FILTER_BY_AVAILABILITY);
      if (FILTER_BY_AVAILABILITY && allow3.size === 0) {
        allow3 = allowedSet(variants, 2, [sel[name1], c2], false);
      }
      const allow3N = new Set(Array.from(allow3).map(n));
      eachValueInDOM(root, name3, (val) => {
        const disable = !allow3N.has(n(val));
        setDisabled(root, name3, val, disable, false);
      });
      const current3 = readSel(root, [name3])[name3];
      if (!current3 || !allow3N.has(n(current3))) {
        const first = Array.from(allow3)[0];
        if (first != null) setChoice(root, name3, first);
        else clickFirstEnabled(root, name3);
      }
    }

    // finalize (id, URL, events)
    finalize(root, names, variants);

    BUSY = false;
  }

  function attach(root) {
    // init
    recalc(root);

    root.addEventListener('change', (e) => {
      if (!e.target.name || !e.target.name.startsWith('options[')) return;
      const name = e.target.name.replace(/^options\[(.*)\]$/, '$1');
      const names = optionNames(root);
      if (!names.length) return;

      if (name === names[0]) {
        recalc(root); // Option 1 changed → cascade 2 & 3
      } else if (name === names[1]) {
        recalc(root); // Option 2 changed → recompute 3
      } else {
        // Option 3 changed → just finalize
        const variants = variantsJSON(root);
        finalize(root, names, variants);
      }
    });

    // in case other scripts fire variant-change
    document.addEventListener('variant-change', (e) => {
      const sec = sectionIdOf(root);
      if (e.detail && e.detail.sectionId && e.detail.sectionId !== sec) return;
      recalc(root);
    });
  }

  function start() {
    document.querySelectorAll('variant-selects').forEach(attach);
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(start, 0);
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
